export default function APIDocsPage() {
  return (
    <div className="bg-secondary-50 min-h-screen py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-secondary-900 mb-4">API Documentation</h1>
          <p className="text-lg text-secondary-600">
            Integrate Beaver data into your applications using our REST API
          </p>
        </div>

        {/* Getting Started */}
        <section className="card p-8 mb-8">
          <h2 className="text-2xl font-bold text-secondary-900 mb-4">Getting Started</h2>
          <p className="text-secondary-600 mb-4">
            The Beaver API provides programmatic access to project data, reservations, and analytics.
            All API requests require authentication using API keys.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-900 mb-2">Base URL</h3>
            <code className="text-sm text-blue-800">https://api.beaver.example.com/v1</code>
          </div>

          <h3 className="font-semibold text-secondary-900 mb-3">Authentication</h3>
          <p className="text-secondary-600 mb-3">
            Include your API key in the Authorization header:
          </p>
          <pre className="bg-secondary-900 text-secondary-100 p-4 rounded-lg overflow-x-auto mb-4">
{`curl https://api.beaver.example.com/v1/projects \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
          </pre>
        </section>

        {/* Endpoints */}
        <section className="space-y-6">
          <h2 className="text-2xl font-bold text-secondary-900">Endpoints</h2>

          {/* Projects */}
          <div className="card p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="badge badge-primary">GET</span>
              <code className="text-lg">/projects</code>
            </div>
            <p className="text-secondary-600 mb-4">Retrieve a list of all published projects</p>

            <h4 className="font-semibold text-secondary-900 mb-2">Query Parameters</h4>
            <table className="w-full text-sm mb-4">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="text-left p-3">Parameter</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t">
                  <td className="p-3"><code>location</code></td>
                  <td className="p-3">string</td>
                  <td className="p-3">Filter by location</td>
                </tr>
                <tr className="border-t">
                  <td className="p-3"><code>min_price</code></td>
                  <td className="p-3">number</td>
                  <td className="p-3">Minimum price range</td>
                </tr>
                <tr className="border-t">
                  <td className="p-3"><code>max_price</code></td>
                  <td className="p-3">number</td>
                  <td className="p-3">Maximum price range</td>
                </tr>
                <tr className="border-t">
                  <td className="p-3"><code>status</code></td>
                  <td className="p-3">string</td>
                  <td className="p-3">Filter by status (activated, published)</td>
                </tr>
              </tbody>
            </table>

            <h4 className="font-semibold text-secondary-900 mb-2">Example Response</h4>
            <pre className="bg-secondary-900 text-secondary-100 p-4 rounded-lg overflow-x-auto text-sm">
{`{
  "data": [
    {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "title": "Harbour View Residences",
      "location": "Auckland CBD",
      "description": "Luxury waterfront apartments...",
      "price_range_min": 850000,
      "price_range_max": 1500000,
      "status": "published",
      "image_urls": ["https://..."],
      "created_at": "2024-01-15T10:30:00Z"
    }
  ],
  "count": 1
}`}
            </pre>
          </div>

          {/* Single Project */}
          <div className="card p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="badge badge-primary">GET</span>
              <code className="text-lg">/projects/:id</code>
            </div>
            <p className="text-secondary-600 mb-4">Retrieve a specific project by ID</p>

            <h4 className="font-semibold text-secondary-900 mb-2">Example Response</h4>
            <pre className="bg-secondary-900 text-secondary-100 p-4 rounded-lg overflow-x-auto text-sm">
{`{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "title": "Harbour View Residences",
    "location": "Auckland CBD",
    "units": [
      {
        "id": "unit-uuid",
        "unit_number": "A101",
        "bedrooms": 2,
        "bathrooms": 2,
        "price": 950000,
        "status": "available"
      }
    ]
  }
}`}
            </pre>
          </div>

          {/* Search */}
          <div className="card p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="badge badge-primary">GET</span>
              <code className="text-lg">/projects/search</code>
            </div>
            <p className="text-secondary-600 mb-4">Full-text search across projects</p>

            <h4 className="font-semibold text-secondary-900 mb-2">Query Parameters</h4>
            <table className="w-full text-sm mb-4">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="text-left p-3">Parameter</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t">
                  <td className="p-3"><code>q</code></td>
                  <td className="p-3">string</td>
                  <td className="p-3">Search query (required)</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Webhooks */}
          <div className="card p-6">
            <h3 className="text-xl font-bold text-secondary-900 mb-4">Webhooks</h3>
            <p className="text-secondary-600 mb-4">
              Receive real-time notifications when events occur on the platform
            </p>

            <h4 className="font-semibold text-secondary-900 mb-2">Available Events</h4>
            <ul className="list-disc list-inside space-y-2 text-secondary-600 mb-4">
              <li><code>project.created</code> - New project published</li>
              <li><code>project.updated</code> - Project details changed</li>
              <li><code>reservation.created</code> - New unit reservation</li>
              <li><code>reservation.converted</code> - Reservation converted to sale</li>
            </ul>

            <h4 className="font-semibold text-secondary-900 mb-2">Payload Example</h4>
            <pre className="bg-secondary-900 text-secondary-100 p-4 rounded-lg overflow-x-auto text-sm">
{`{
  "event": "project.created",
  "timestamp": "2024-01-15T10:30:00Z",
  "data": {
    "project_id": "123e4567-e89b-12d3-a456-426614174000",
    "title": "Harbour View Residences"
  }
}`}
            </pre>
          </div>
        </section>

        {/* Rate Limits */}
        <section className="card p-8 mt-8">
          <h2 className="text-2xl font-bold text-secondary-900 mb-4">Rate Limits</h2>
          <p className="text-secondary-600 mb-4">
            API requests are rate-limited to ensure fair usage:
          </p>
          <ul className="list-disc list-inside space-y-2 text-secondary-600">
            <li>1000 requests per hour for standard API keys</li>
            <li>Rate limit headers included in all responses</li>
            <li>429 status code returned when limit exceeded</li>
          </ul>
        </section>

        {/* Support */}
        <section className="card p-8 mt-8 bg-gradient-to-br from-primary-50 to-blue-50 border-primary-200">
          <h2 className="text-2xl font-bold text-secondary-900 mb-4">Need Help?</h2>
          <p className="text-secondary-600 mb-4">
            Our developer support team is here to help you integrate successfully.
          </p>
          <a
            href="mailto:api@beaver.example.com"
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
          >
            Contact API Support
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </a>
        </section>
      </div>
    </div>
  )
}
